import type { PrintLog } from '@features/printer/types';

import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { History, ExternalLink } from 'lucide-react';

import { Card } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { SectionHeader } from '@shared/components/section-header';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip';
import { Table, TableRow, TableBody, TableCell, TableHead, TableHeader } from '@shared/components/ui/table';

import { formatDuration } from '@features/printer/utils/print-format';
import { usePrinterLog } from '@features/printer/hooks/use-printer-log';

const formatTime = (date: string | Date): string => {
  const dateTime = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  return dateTime.isValid ? dateTime.toFormat('HH:mm:ss') : '-';
};

const STATUS: Record<
  NonNullable<PrintLog['status']>,
  { label: string; variant: 'success' | 'destructive' | 'warning' | 'secondary' }
> = {
  sent: { label: 'Envoyé', variant: 'success' },
  failed: { label: 'Échec', variant: 'destructive' },
  skipped: { label: 'Sans imprimante', variant: 'warning' },
  pending: { label: 'En cours', variant: 'secondary' },
};

const StatusCell = ({ log }: { log: PrintLog }) => {
  if (!log.status) {
    return <span className="text-muted-foreground">-</span>;
  }
  const { label, variant } = STATUS[log.status];
  const badge = (
    <Badge size="sm" variant={variant}>
      {label}
    </Badge>
  );

  return log.error ? (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{log.error}</TooltipContent>
    </Tooltip>
  ) : (
    badge
  );
};

const HEAD_CLASS = 'sticky top-0 z-10 h-10 py-1.5';
const CELL_CLASS = 'py-1.5';

export const PrintHistory = () => {
  const { logs } = usePrinterLog();

  // Logs are appended chronologically: reverse them to show the most recent first
  const sortedLogs = useMemo(() => [...logs].reverse(), [logs]);

  if (logs.length === 0) {
    return null;
  }

  return (
    <Card className="mt-10 gap-0 py-0">
      <SectionHeader
        icon={<History />}
        subtitle={`${logs.length} impression${logs.length > 1 ? 's' : ''} · la plus récente en haut`}
        title="Historique des impressions"
      />
      <Table containerClassName="max-h-100 overflow-y-auto">
        <TableHeader>
          <TableRow>
            <TableHead className={`${HEAD_CLASS} w-25`}>Heure</TableHead>
            <TableHead className={HEAD_CLASS}>Référence</TableHead>
            <TableHead className={HEAD_CLASS}>Document</TableHead>
            <TableHead className={HEAD_CLASS}>Imprimante</TableHead>
            <TableHead className={`${HEAD_CLASS} w-32`}>Statut</TableHead>
            <TableHead className={`${HEAD_CLASS} w-24 text-right`}>Envoi</TableHead>
            <TableHead className={`${HEAD_CLASS} w-16`} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedLogs.map((row, index) => (
            <TableRow
              className="hover:bg-accent/50"
              data-state={index === 0 ? 'selected' : undefined}
              key={logs.length - index}
            >
              <TableCell className={`${CELL_CLASS} font-mono text-muted-foreground`}>{formatTime(row.date)}</TableCell>
              <TableCell className={`${CELL_CLASS} font-semibold`}>{row.name}</TableCell>
              <TableCell className={CELL_CLASS}>
                <Badge size="sm" variant="soft">
                  {row.type}
                </Badge>
              </TableCell>
              <TableCell className={`${CELL_CLASS} max-w-48 truncate font-mono text-xs text-muted-foreground`}>
                {row.printer ?? '-'}
              </TableCell>
              <TableCell className={CELL_CLASS}>
                <StatusCell log={row} />
              </TableCell>
              <TableCell className={`${CELL_CLASS} text-right font-mono text-sm text-muted-foreground`}>
                {formatDuration(row.durationMs)}
              </TableCell>
              <TableCell className={`${CELL_CLASS} text-right`}>
                {row.link && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild size="icon-sm" variant="ghost">
                        <a aria-label="Ouvrir le document" href={row.link} rel="noopener noreferrer" target="_blank">
                          <ExternalLink />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ouvrir le document</TooltipContent>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
